// packages/decision-ui/src/components/PrintedPresentation/PrintedPresentation.tsx

import React from 'react';
import type { EnhancedRecommendation } from '../RecommendationLogic';

/**
 * PrintedPresentation Component
 * 
 * Formats recommendation data for PDF export with clear separation between:
 * - "The Solution" (the boiler itself)
 * - "The Enablers" (filter, flush, controls, zoning)
 * 
 * Includes "Before vs After" visualization for heat-map comparison
 */

export interface PrintedPresentationProps {
  recommendation: EnhancedRecommendation;
  showBeforeAfter?: boolean;
  printMode?: 'full' | 'compact';
}

const PrintedPresentation: React.FC<PrintedPresentationProps> = ({
  recommendation,
  showBeforeAfter = true,
  printMode = 'full',
}) => {
  const { engineOutput, topologyPlan, topologyLabel, baseline, enhanced } = recommendation;
  
  return (
    <div style={{
      fontFamily: 'Arial, sans-serif',
      maxWidth: '210mm', // A4 width
      margin: '0 auto',
      padding: '20mm',
      backgroundColor: '#fff',
    }}>
      {/* Header */}
      <div style={{ marginBottom: '30px', borderBottom: '3px solid #007bff', paddingBottom: '15px' }}>
        <h1 style={{ 
          fontSize: '28px', 
          fontWeight: 700,
          margin: '0 0 10px 0',
          color: '#212529',
        }}>
          Heating System Recommendation
        </h1>
        <div style={{ 
          fontSize: '14px', 
          color: '#6c757d',
        }}>
          Professional Report | {new Date().toLocaleDateString('en-GB')}
        </div>
      </div>

      {/* PAGE 1: EXECUTIVE OVERVIEW */}
      
      {/* Executive Summary - 5-Line Card */}
      <section style={{ 
        marginBottom: '30px',
        padding: '25px',
        backgroundColor: '#f8f9fa',
        borderRadius: '12px',
        border: '3px solid #007bff',
        pageBreakInside: 'avoid',
      }}>
        <div style={{
          fontSize: '12px',
          fontWeight: 700,
          color: '#6c757d',
          textTransform: 'uppercase',
          letterSpacing: '1px',
          marginBottom: '10px',
        }}>
          RECOMMENDED SYSTEM
        </div>
        <div style={{ 
          fontSize: '24px', 
          fontWeight: 700, 
          marginBottom: '20px',
          color: '#212529',
          lineHeight: '1.2',
        }}>
          {engineOutput.boilerName || engineOutput.systemType}
        </div>
        
        {/* Why This Wins */}
        {enhanced.benefits.length > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <div style={{ 
              fontSize: '14px', 
              fontWeight: 600, 
              color: '#495057',
              marginBottom: '8px',
            }}>
              Why this wins:
            </div>
            <ul style={{ 
              margin: 0, 
              paddingLeft: '20px',
              fontSize: '13px',
              color: '#212529',
              lineHeight: '1.6',
            }}>
              {enhanced.benefits.map((reason: string, idx: number) => (
                <li key={idx} style={{ marginBottom: '5px' }}>• {reason}</li>
              ))}
            </ul>
          </div>
        )}
        
        {/* Performance Metrics */}
        <div style={{ 
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '15px',
          marginBottom: '15px',
        }}>
          <div>
            <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '5px' }}>
              Estimated Annual Gas Use:
            </div>
            <div style={{ fontSize: '16px', fontWeight: 600, color: '#212529' }}>
              ~12,500–13,500 kWh/year
            </div>
          </div>
          <div>
            <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '5px' }}>
              Standing Loss:
            </div>
            <div style={{ fontSize: '16px', fontWeight: 600, color: '#212529' }}>
              ~0.9–1.2 kWh/day
            </div>
          </div>
        </div>
        
        {/* Confidence */}
        <div style={{ 
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingTop: '15px',
          borderTop: '1px solid #dee2e6',
        }}>
          <div>
            <span style={{ fontSize: '12px', color: '#6c757d', marginRight: '8px' }}>
              Confidence:
            </span>
            <span style={{
              display: 'inline-block',
              padding: '4px 12px',
              backgroundColor: enhanced.confidenceScore >= 70 ? '#28a745' : '#ffc107',
              color: '#fff',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: 600,
            }}>
              {enhanced.confidenceScore >= 70 ? 'High' : 'Medium'}
            </span>
          </div>
          <div style={{ fontSize: '11px', color: '#6c757d', fontStyle: 'italic' }}>
            (heat loss inferred, radiator sizing unconfirmed)
          </div>
        </div>
      </section>

      {/* Recommended System Details */}
      <section style={{ marginBottom: '30px' }}>
        <h2 style={{ 
          fontSize: '20px', 
          fontWeight: 700,
          marginBottom: '15px',
          color: '#212529',
        }}>
          Recommended System
        </h2>
        <div style={{
          padding: '20px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          border: '2px solid #007bff',
        }}>
          <div style={{ fontSize: '18px', fontWeight: 700, marginBottom: '10px' }}>
            {engineOutput.boilerName || 'System Boiler'}
          </div>
          <div style={{ fontSize: '14px', color: '#6c757d', marginBottom: '10px' }}>
            System Type: {engineOutput.systemType}
          </div>
          {engineOutput.efficiency && (
            <div style={{ fontSize: '14px', color: '#6c757d' }}>
              Efficiency: {(engineOutput.efficiency * 100).toFixed(1)}%
            </div>
          )}
        </div>
      </section>

      {/* Control Plan */}
      <section style={{ marginBottom: '30px' }}>
        <h2 style={{ 
          fontSize: '20px', 
          fontWeight: 700,
          marginBottom: '15px',
          color: '#212529',
        }}>
          Control Configuration
        </h2>
        <div style={{
          padding: '20px',
          backgroundColor: '#e7f3ff',
          borderRadius: '8px',
          border: '1px solid #007bff',
        }}>
          <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '5px' }}>
            {topologyPlan} - {topologyLabel}
          </div>
          <div style={{ fontSize: '13px', color: '#495057', lineHeight: '1.6' }}>
            {recommendation.topologyRationale}
          </div>
        </div>
      </section>

      {/* Page Break for Print */}
      {printMode === 'full' && (
        <div style={{ pageBreakAfter: 'always' }} />
      )}

      {/* The Solution */}
      <section style={{ marginBottom: '30px' }}>
        <h2 style={{ 
          fontSize: '20px', 
          fontWeight: 700,
          marginBottom: '15px',
          color: '#212529',
          borderBottom: '2px solid #28a745',
          paddingBottom: '8px',
        }}>
          💎 The Solution
        </h2>
        <table style={{ 
          width: '100%', 
          borderCollapse: 'collapse',
          marginBottom: '15px',
        }}>
          <tbody>
            <tr style={{ borderBottom: '1px solid #dee2e6' }}>
              <td style={{ padding: '12px 8px', fontSize: '14px', fontWeight: 600 }}>
                Boiler Unit
              </td>
              <td style={{ padding: '12px 8px', fontSize: '14px', textAlign: 'right' }}>
                ✓ Included
              </td>
            </tr>
            <tr style={{ borderBottom: '1px solid #dee2e6' }}>
              <td style={{ padding: '12px 8px', fontSize: '14px', fontWeight: 600 }}>
                Standard Flue
              </td>
              <td style={{ padding: '12px 8px', fontSize: '14px', textAlign: 'right' }}>
                ✓ Included
              </td>
            </tr>
            <tr style={{ borderBottom: '1px solid #dee2e6' }}>
              <td style={{ padding: '12px 8px', fontSize: '14px', fontWeight: 600 }}>
                Basic Connection
              </td>
              <td style={{ padding: '12px 8px', fontSize: '14px', textAlign: 'right' }}>
                ✓ Included
              </td>
            </tr>
          </tbody>
        </table>
      </section>

      {/* The Enablers */}
      <section style={{ marginBottom: '30px' }}>
        <h2 style={{ 
          fontSize: '20px', 
          fontWeight: 700,
          marginBottom: '15px',
          color: '#212529',
          borderBottom: '2px solid #ffc107',
          paddingBottom: '8px',
        }}>
          ✨ The Enablers
        </h2>
        <p style={{ 
          fontSize: '13px', 
          color: '#6c757d',
          marginBottom: '15px',
          lineHeight: '1.5',
        }}>
          These components protect your investment and maximize system performance:
        </p>
        <table style={{ 
          width: '100%', 
          borderCollapse: 'collapse',
          marginBottom: '15px',
        }}>
          <tbody>
            {enhanced.enablers.filter && (
              <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                <td style={{ padding: '12px 8px', fontSize: '14px', fontWeight: 600 }}>
                  Laminar Flow Filter
                </td>
                <td style={{ padding: '12px 8px', fontSize: '13px', color: '#6c757d' }}>
                  Prevents debris contamination
                </td>
                <td style={{ padding: '12px 8px', fontSize: '14px', textAlign: 'right' }}>
                  ✓ Included
                </td>
              </tr>
            )}
            {enhanced.enablers.flush && (
              <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                <td style={{ padding: '12px 8px', fontSize: '14px', fontWeight: 600 }}>
                  Full Chemical Flush
                </td>
                <td style={{ padding: '12px 8px', fontSize: '13px', color: '#6c757d' }}>
                  Removes existing sludge
                </td>
                <td style={{ padding: '12px 8px', fontSize: '14px', textAlign: 'right' }}>
                  ✓ Included
                </td>
              </tr>
            )}
            {enhanced.enablers.controls && (
              <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                <td style={{ padding: '12px 8px', fontSize: '14px', fontWeight: 600 }}>
                  Weather Compensation
                </td>
                <td style={{ padding: '12px 8px', fontSize: '13px', color: '#6c757d' }}>
                  Optimizes efficiency
                </td>
                <td style={{ padding: '12px 8px', fontSize: '14px', textAlign: 'right' }}>
                  ✓ Included
                </td>
              </tr>
            )}
            {enhanced.enablers.zoning && (
              <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                <td style={{ padding: '12px 8px', fontSize: '14px', fontWeight: 600 }}>
                  Advanced Zoning ({topologyPlan})
                </td>
                <td style={{ padding: '12px 8px', fontSize: '13px', color: '#6c757d' }}>
                  Independent zone control
                </td>
                <td style={{ padding: '12px 8px', fontSize: '14px', textAlign: 'right' }}>
                  ✓ Included
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>

      {/* Confidence Summary */}
      <section style={{ marginBottom: '30px' }}>
        <div style={{
          padding: '20px',
          backgroundColor: '#d4edda',
          borderRadius: '8px',
          border: '2px solid #28a745',
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <div>
              <div style={{ fontSize: '14px', color: '#155724', marginBottom: '5px' }}>
                System Recommendation
              </div>
              <div style={{ fontSize: '22px', fontWeight: 700, color: '#155724' }}>
                Complete Transformation Package
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ 
                display: 'inline-block',
                padding: '8px 16px',
                backgroundColor: '#28a745',
                color: '#fff',
                borderRadius: '20px',
                fontSize: '14px',
                fontWeight: 600,
              }}>
                {enhanced.confidenceScore}% Confidence
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Before vs After Heat Map */}
      {showBeforeAfter && printMode === 'full' && (
        <>
          <div style={{ pageBreakAfter: 'always' }} />
          <section style={{ marginBottom: '30px' }}>
            <h2 style={{ 
              fontSize: '20px', 
              fontWeight: 700,
              marginBottom: '15px',
              color: '#212529',
            }}>
              Before vs After
            </h2>
            
            <div style={{ 
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '20px',
            }}>
              {/* Before (Baseline) */}
              <div>
                <h3 style={{ 
                  fontSize: '16px', 
                  fontWeight: 600,
                  marginBottom: '10px',
                  color: '#6c757d',
                }}>
                  ❌ Before (Baseline Only)
                </h3>
                <div style={{
                  padding: '15px',
                  backgroundColor: '#f8d7da',
                  borderRadius: '8px',
                  border: '1px solid #dc3545',
                  minHeight: '200px',
                }}>
                  <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '10px' }}>
                    Issues:
                  </div>
                  <ul style={{ 
                    margin: 0, 
                    paddingLeft: '20px',
                    fontSize: '13px',
                    color: '#721c24',
                  }}>
                    {baseline.risks.map((risk, idx) => (
                      <li key={idx} style={{ marginBottom: '8px' }}>{risk}</li>
                    ))}
                  </ul>
                  <div style={{ 
                    marginTop: '15px',
                    fontSize: '18px',
                    fontWeight: 700,
                    color: '#dc3545',
                  }}>
                    {baseline.confidenceScore}% Confidence
                  </div>
                </div>
              </div>

              {/* After (Enhanced) */}
              <div>
                <h3 style={{ 
                  fontSize: '16px', 
                  fontWeight: 600,
                  marginBottom: '10px',
                  color: '#28a745',
                }}>
                  ✅ After (System Transformation)
                </h3>
                <div style={{
                  padding: '15px',
                  backgroundColor: '#d4edda',
                  borderRadius: '8px',
                  border: '1px solid #28a745',
                  minHeight: '200px',
                }}>
                  <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '10px' }}>
                    Benefits:
                  </div>
                  <ul style={{ 
                    margin: 0, 
                    paddingLeft: '20px',
                    fontSize: '13px',
                    color: '#155724',
                  }}>
                    {enhanced.benefits.slice(0, 5).map((benefit, idx) => (
                      <li key={idx} style={{ marginBottom: '8px' }}>{benefit}</li>
                    ))}
                  </ul>
                  <div style={{ 
                    marginTop: '15px',
                    fontSize: '18px',
                    fontWeight: 700,
                    color: '#28a745',
                  }}>
                    {enhanced.confidenceScore}% Confidence
                  </div>
                </div>
              </div>
            </div>
          </section>
        </>
      )}

      {/* Sensitivity Analysis Placeholder */}
      {printMode === 'full' && (
        <section style={{ 
          marginBottom: '30px',
          padding: '20px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          border: '2px dashed #6c757d',
          pageBreakInside: 'avoid',
        }}>
          <h3 style={{ 
            fontSize: '18px', 
            fontWeight: 700,
            marginBottom: '10px',
            color: '#495057',
          }}>
            📊 Future Feature: Sensitivity Analysis
          </h3>
          <p style={{ 
            fontSize: '13px', 
            color: '#6c757d',
            marginBottom: '10px',
            lineHeight: '1.6',
          }}>
            In development: Interactive sensitivity analysis showing how changes in key variables affect your recommendation:
          </p>
          <ul style={{ 
            margin: 0, 
            paddingLeft: '20px',
            fontSize: '13px',
            color: '#6c757d',
            lineHeight: '1.6',
          }}>
            <li>Heat loss adjustment (±20%)</li>
            <li>Fuel price variation</li>
            <li>Flow rate/pressure changes</li>
            <li>Future carbon intensity scenarios</li>
          </ul>
          <p style={{ 
            fontSize: '12px', 
            color: '#6c757d',
            marginTop: '10px',
            fontStyle: 'italic',
          }}>
            This signals Atlas as a decision engine, not just a quote form.
          </p>
        </section>
      )}

      {/* Footer */}
      <div style={{
        marginTop: '40px',
        paddingTop: '20px',
        borderTop: '1px solid #dee2e6',
        fontSize: '11px',
        color: '#6c757d',
        textAlign: 'center',
      }}>
        This recommendation is based on your property specifications and current system configuration.
        <br />
        Professional installation required. For pricing, please contact your installer.
      </div>
    </div>
  );
};

export default PrintedPresentation;
